import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  FolderTree,
  Users,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Tier-2 content piece targeting "folder structure for client projects" and
 * variants ("client folder template", "consistent project folders across a
 * team"). The wedge: anyone can sketch a folder template once, but two things
 * break it. Applying it to every new client by hand is tedious, and back-filling
 * the existing mess into it is the part nobody ever finishes. Sortio applies the
 * template to a new client folder instantly and back-fills the legacy pile by
 * reading file content. Comparison is hand-built folders vs template + AI
 * back-fill. Cites Stanford Libraries on consistent, descriptive naming.
 */

const PUBLISHED = '2026-06-05';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/folder-structure-client-projects';

const FAQ = [
  {
    question: 'What is the best folder structure for client projects?',
    answer:
      'The best structure is one repeatable template applied identically to every client: Client > Project > phase or category. At the top you have a folder per client. Inside each client, a folder per engagement or project. Inside each project, a fixed set of subfolders in a fixed order (01_Admin, 02_Inputs, 03_Working, 04_Deliverables, 05_Final). The exact subfolders matter less than the fact that they are the same for every project, so anyone on the team can find anything without asking. Sortio can stamp this template onto a new client folder instantly and back-fill an existing mess into it by reading file content.',
  },
  {
    question: 'How many levels deep should a client folder structure go?',
    answer:
      'Three levels is the sweet spot for most teams: Client, Project, then category. Two levels (Client then loose files) gets messy the moment a client has more than one engagement. Four or more levels (Client > Year > Project > Phase > Subtask) looks tidy in a diagram but no one maintains it, and deep paths break links and hit path-length limits on Windows. Pick three, keep the third level a fixed short list, and resist the urge to nest further.',
  },
  {
    question: 'Why does a consistent folder structure matter?',
    answer:
      'Because the cost of an inconsistent structure is paid every single day in small amounts: time spent guessing where a file went, time spent asking a colleague, files saved to the wrong place and then effectively lost, and the slow erosion of trust that the shared drive is reliable. Stanford University Libraries puts the principle plainly: you should be consistent and descriptive in naming and organizing files so that it is obvious where to find specific data and what the files contain. Consistency is what lets a structure scale past one person.',
  },
  {
    question: 'How do I keep the folder structure consistent across a whole team?',
    answer:
      'Two things. First, write the template down so it is not living in one person\'s head. Second, automate the enforcement so consistency does not depend on everyone remembering. On Sortio Teams ($29 per seat per month) you publish a shared rule that defines the template and the naming pattern, and every member\'s machine applies the same logic. New files get routed into the right client and project folder regardless of who saved them or what they named the file.',
  },
  {
    question: 'How do I migrate an existing messy project folder into a new template?',
    answer:
      'This is the part people dread and skip. The manual version is opening each file to figure out which client and project it belongs to, then dragging it into place, for thousands of files. Sortio does the back-fill by reading content: point it at the old pile, give it the template plus a rule (identify the client and the project from each file, then file it under the matching folder), run Preview to check the proposed destinations, and apply. The legacy mess lands inside the new structure in one pass instead of a migration that never finishes.',
  },
  {
    question: 'Should the folder name or the file name carry the metadata?',
    answer:
      'Both, with no overlap. The folder path carries the client and project (Acme/Website-Redesign/). The file name carries the date, type, and version (2026-06-05_Proposal_v2.pdf). Do not repeat the client name in every file name when it is already in the path, and do not bury the project inside file names when a folder would make it scannable. A clean naming pattern at the file level plus a clean folder path is what makes a drive searchable.',
  },
  {
    question: 'What naming pattern should files inside each project use?',
    answer:
      'A pattern that sorts and skims well is {YYYY-MM-DD}_{DocType}_{vN}.ext, for example 2026-06-05_SOW_v1.pdf or 2026-05-30_Invoice_v2.pdf. ISO date first so files sort chronologically, document type next so the folder is scannable by eye, version last so revisions stay together. Keep the vocabulary for DocType short and fixed (SOW, Proposal, Invoice, Contract, Notes, Deliverable) so it stays enforceable.',
  },
];

const FolderStructureClientProjectsBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Consistent folder structure for client projects', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Consistent folder structure for client projects',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Set Up a Consistent Folder Structure for Client Projects',
    description:
      'Design one repeatable per-client, per-project folder template, then auto-file existing files into it. Copyable folder tree, naming pattern, and a working Sortio prompt that applies the template and back-fills the legacy mess by reading content.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    author: {
      '@type': 'Organization',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Set Up a Consistent Folder Structure for Client Projects"
        description="Design one repeatable per-client, per-project folder template, then auto-file your existing files into it. Copyable folder tree, naming pattern, and a working Sortio prompt that back-fills the mess by reading content."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="folder structure for client projects, client folder template, project folder structure, consistent folder structure, file organization for agencies, folder structure across a team, client project folders, naming convention client files"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Set Up a Consistent Folder Structure for Client Projects
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span>By the Sortio team</span>
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>9 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Every agency, firm, and freelancer hits the same wall. The first three clients live
            in neat folders. By the tenth, one client is "AcmeCorp", another is "Acme Corp
            (final)", a third is "acme_2026", and half the files are loose at the top level
            because someone was in a hurry. Nobody decided to be inconsistent. It just happened,
            one rushed save at a time, until finding last quarter's signed SOW means asking three
            people and opening four wrong folders first.
          </p>

          <p>
            The fix is not willpower. It is a template you define once and then apply by default.
            This post gives you a concrete, copyable folder tree (Client > Project > phase
            or category), a file naming pattern that survives, the design principles behind both,
            and the part everyone skips: how to back-fill the existing mess into the new structure
            without hand-sorting thousands of files.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Define one repeatable template, Client > Project > a fixed set of
                  subfolders, and apply it identically to every client. Pair it with a file
                  naming pattern like {'{YYYY-MM-DD}_{DocType}_{vN}.ext'}. Sortio stamps the
                  template onto a new client folder instantly, and back-fills your existing pile
                  into it by reading each file's content to decide which client and project it
                  belongs to. On Teams, a shared rule keeps the structure consistent across
                  everyone's machine.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The best folder structure for client projects
          </h2>

          <p>
            The best folder structure for client projects is one repeatable template applied
            identically to every client: Client > Project > phase or category. The exact
            subfolders matter far less than the fact that they are the same for every project, so
            any team member can find any file without asking. Sortio can stamp this template onto
            a new client folder instantly and back-fill an existing mess into it by reading what
            is inside each file.
          </p>

          <p>
            Here is a concrete, copyable tree. Three levels deep, fixed third level, ordered with
            number prefixes so the subfolders always sort the same way:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Clients/
  Acme-Corp/
    Website-Redesign/
      01_Admin/          (contracts, SOWs, change orders)
      02_Inputs/         (client-supplied assets, briefs)
      03_Working/        (drafts, work in progress)
      04_Deliverables/   (what you send the client)
      05_Final/          (signed-off, archived versions)
    Brand-Refresh/
      01_Admin/
      02_Inputs/
      03_Working/
      04_Deliverables/
      05_Final/
  Globex-LLC/
    Q3-Marketing/
      01_Admin/
      02_Inputs/
      03_Working/
      04_Deliverables/
      05_Final/`}
          </pre>

          <p>
            Two top-level rules make this hold up. Pick one canonical form for client names
            (Acme-Corp, never AcmeCorp or "Acme Corp (final)") and never deviate. Keep the
            third-level subfolder list short and identical for every project, because the moment
            two projects have different subfolders the "find anything without asking" property is
            gone.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Design principles that keep a template alive
          </h2>

          <p>
            A template is only useful if people actually follow it, so the design choices are
            really about reducing the chance of drift. A few principles do most of the work:
          </p>

          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-apple-100">Three levels, no more.</strong> Client, Project,
              category. Deeper nesting looks organized in a diagram but no one maintains it, and
              long paths break links and hit Windows path-length limits.
            </li>
            <li>
              <strong className="text-apple-100">Fixed, ordered subfolders.</strong> Number
              prefixes (01_, 02_) force a consistent sort order and signal that the list is
              closed. People add ad-hoc folders only when the existing ones feel insufficient.
            </li>
            <li>
              <strong className="text-apple-100">One canonical name per client.</strong> Decide
              the spelling, casing, and separators once. Hyphens beat spaces (they survive URLs
              and command lines) and beat underscores for readability.
            </li>
            <li>
              <strong className="text-apple-100">Folders carry the where, file names carry the
              what.</strong> The path says Acme-Corp / Website-Redesign. The file name says the
              date, type, and version. No repeating the client name in every file.
            </li>
            <li>
              <strong className="text-apple-100">Write it down, then automate it.</strong> A
              template living in one person's head is a template that dies when they go on
              vacation.
            </li>
          </ul>

          <p>
            The naming half of the system matters as much as the folders. Inside each project,
            use a single pattern for every file:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Pattern:  {YYYY-MM-DD}_{DocType}_{vN}.ext

2026-06-05_SOW_v1.pdf
2026-06-12_Proposal_v2.pdf
2026-05-30_Invoice_v1.pdf
2026-06-01_Deliverable_v3.zip

DocType vocabulary (fixed, short):
SOW, Proposal, Contract, Invoice, Brief,
Notes, Deliverable, Final`}
          </pre>

          <p>
            ISO date first so files sort chronologically without a custom view. Document type
            next so the folder is scannable by eye. Version last so revisions of the same
            document stay grouped. The reason to fix the DocType vocabulary is the same reason to
            fix the subfolder list: a short closed set is enforceable, an open set drifts. There
            is a deeper guide on this in our piece on{' '}
            <Link to="/blog/enforce-file-naming-convention" className="text-sortio-primary hover:underline">
              how to enforce a file naming convention
            </Link>{' '}
            if you want to formalize the rules.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Why consistency matters more than the exact layout
          </h2>

          <p>
            People burn a surprising amount of energy debating the perfect taxonomy and almost
            none on whether it will actually be followed. That is backwards. A mediocre structure
            applied consistently beats a brilliant structure applied half the time, because the
            value is entirely in predictability. If you can guess where a file is without looking,
            the structure is working, regardless of how elegant it is.
          </p>

          <p>
            This is not just folk wisdom. Stanford University Libraries states the principle
            directly in its research data guidance:{' '}
            <a
              href="https://guides.library.stanford.edu/data-best-practices"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sortio-primary hover:underline"
            >
              "You should be consistent and descriptive in naming and organizing files so that it
              is obvious where to find specific data and what the files contain."
            </a>{' '}
            Consistency and descriptiveness are the two levers. A template gives you both at once:
            consistent because it is the same every time, descriptive because the path and name
            together tell you exactly what a file is.
          </p>

          <p>
            The cost of inconsistency is paid in small daily increments that never show up as a
            single fixable problem: a few minutes guessing where something went, a Slack message
            to a colleague, a file saved to the wrong place and quietly lost. It compounds fastest
            on shared drives, where every person's small deviation becomes everyone else's
            confusion. If your shared drive is already in that state, the companion piece on{' '}
            <Link to="/blog/clean-up-messy-shared-drive" className="text-sortio-primary hover:underline">
              how to clean up a messy shared drive
            </Link>{' '}
            covers the cleanup pass before you lock in a template.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Hand-built folders vs template plus AI back-fill
          </h2>

          <p>
            There are two ways to get from "messy" to "consistent template." One is the way
            everyone tries first and abandons. The other is what actually finishes.
          </p>

          <div className="overflow-x-auto not-prose my-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-apple-700/50">
                  <th className="text-left py-3 pr-4 text-apple-100 font-medium">Step</th>
                  <th className="text-left py-3 pr-4 text-apple-100 font-medium">
                    Hand-built folders
                  </th>
                  <th className="text-left py-3 text-apple-100 font-medium">
                    Template + AI back-fill (Sortio)
                  </th>
                </tr>
              </thead>
              <tbody className="text-apple-300">
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top">New client folder</td>
                  <td className="py-3 pr-4 align-top">
                    Recreate the subfolders by hand each time, or copy a "template" folder and
                    hope it stayed current.
                  </td>
                  <td className="py-3 align-top">
                    Stamp the template onto the new client folder in one action. Always current.
                  </td>
                </tr>
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top">Filing a new document</td>
                  <td className="py-3 pr-4 align-top">
                    Each person decides where it goes, and where two people disagree the structure
                    drifts.
                  </td>
                  <td className="py-3 align-top">
                    A rule reads the file and routes it to the right client, project, and
                    subfolder automatically.
                  </td>
                </tr>
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top">Migrating the legacy pile</td>
                  <td className="py-3 pr-4 align-top">
                    Open thousands of files to figure out client and project, then drag each one.
                    Never finishes.
                  </td>
                  <td className="py-3 align-top">
                    Sortio reads content, proposes a destination per file, you Preview and apply
                    in one pass.
                  </td>
                </tr>
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top">Team consistency</td>
                  <td className="py-3 pr-4 align-top">
                    Depends on everyone remembering the convention. Erodes over time.
                  </td>
                  <td className="py-3 align-top">
                    A shared rule enforces the same template on every machine.
                  </td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 align-top">Safety</td>
                  <td className="py-3 pr-4 align-top">
                    Drag-and-drop mistakes are silent and hard to undo.
                  </td>
                  <td className="py-3 align-top">
                    Preview before apply, non-destructive, backup archives of your most recent
                    sorts with in-app undo.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            The hand-built column is not wrong, it is just unfinishable at scale. The reason the
            back-fill never gets done by hand is that it requires reading every file to decide
            where it belongs, and that is precisely the work Sortio automates.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A working Sortio prompt that applies the template and back-fills the mess
          </h2>

          <p>
            Point Sortio at your old, mixed pile of client files (the Downloads dumping ground,
            the "to file" folder, the loose files at the top of the shared drive) and give it the
            template plus a routing rule. Run Preview before applying.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each file and identify which client and which
project it belongs to from its content (the company
name, the project name, the matter, or the engagement
referenced inside it).

File it into this structure:
~/Clients/{Client}/{Project}/{Category}/

Category is one of these fixed subfolders, chosen by
what the file is:
  01_Admin        contracts, SOWs, change orders, invoices
  02_Inputs       client-supplied assets, briefs, source files
  03_Working      drafts and work in progress
  04_Deliverables files prepared to send to the client
  05_Final        signed-off or archived final versions

Rename each file to:
{YYYY-MM-DD}_{DocType}_{vN}.ext
DocType is one of: SOW, Proposal, Contract, Invoice,
Brief, Notes, Deliverable, Final. Use the document date
from the content; if there is none, use the file's
modified date. Default version to v1 unless the content
shows a later revision.

Use one canonical client name per company (Acme-Corp,
never "Acme Corp" or "AcmeCorp"). If you cannot
confidently identify the client, leave the file in
~/Clients/_Unsorted/ for manual review.`}
          </pre>

          <p>
            Preview shows you the proposed client, project, subfolder, and new file name for every
            file, plus the values it pulled from the content. If Sortio misreads a client on one
            file, untick it in the preview so it stays where it is, or refine the prompt and run
            Preview again. Nothing moves until you apply. The {'_Unsorted'} bucket means ambiguous files surface for a
            human instead of getting filed wrong silently.
          </p>

          <p>
            Apply commits the moves. Nothing is destructive: Sortio keeps backup archives of your
            most recent sorts, so a wrong call can be undone from inside the app. For a deeper look at
            running this per client rather than across the whole pile at once, see{' '}
            <Link
              to="/blog/batch-organize-documents-per-client"
              className="text-sortio-primary hover:underline"
            >
              how to batch organize documents per client
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Keeping the structure consistent across a team
          </h2>

          <p>
            A template that one person enforces is a template that decays the day they get busy.
            The only durable version is automated enforcement that does not depend on memory. On
            Sortio Teams ($29 per seat per month) you publish a shared rule that encodes the
            template and the naming pattern, and it applies the same logic on every member's
            machine. It does not matter who saves a file or what they name it; the rule reads the
            content and routes it to the right client, project, and subfolder.
          </p>

          <p>
            For the files people genuinely cannot misfile, you can promote the deterministic parts
            to the AI Rule Builder, which generates a rule that does not use your AI allowance at all.
            Pair that with a watch folder on Pro or Teams so new files arriving in the intake
            location get filed automatically, and you can run the watch in Preview mode first so
            it queues proposed moves and notifies you instead of applying until you trust it.
            That combination, shared rule plus watch folder, is what turns a template from a
            document people are supposed to follow into the path of least resistance.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/enforce-file-naming-convention"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to enforce a file naming convention
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/clean-up-messy-shared-drive"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to clean up a messy shared drive
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/batch-organize-documents-per-client"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to batch organize documents per client
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Apply your template in one pass</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance, no card required. Point Sortio at a pile
              of mixed client files, paste the template prompt, and run Preview to see exactly
              where every file would land before anything moves.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/download"
                className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
              >
                Download Sortio
                <ArrowRight className="ml-2" size={16} />
              </Link>
              <Link
                to="/business"
                className="inline-flex items-center px-6 py-3 bg-apple-700/40 hover:bg-apple-700/60 text-apple-100 font-medium rounded-xl transition-colors duration-200"
              >
                <Users className="mr-2" size={16} />
                Sortio for teams
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FolderStructureClientProjectsBlogPage;
